import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map, publishReplay, refCount, skipWhile, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { RouterService } from '@nimbus-library/features/navigation/router.service';
import { ModalService } from '@nimbus-library/features/modal/services/modal.service';
import { TranslationObserverService } from '@nimbus-library/features/localization/services/translation-observer.service';
import { ChartTheme } from '@nimbus-library/features/charts/models/char-theme.enum';
import { CommonThemeActions } from '@nimbus-library/features/shared/store/theme-store/common-theme.actions';
import { CommonThemeState } from '@nimbus-library/features/shared/store/theme-store/common-theme.state';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';
import { ClientType } from '@nimbus-library/features/microsoft-teams/models/client-type';
import { ITeamsContext } from '@nimbus-library/features/microsoft-teams/models/i-teams.context';
import { TeamState } from '@nimbus-portal/common/store/teams/teams.state';

export interface AppRouteData {
  loadContext: boolean;
  loadRole: boolean;
}

export abstract class IInitializationService {
  public readonly routeData$: Observable<AppRouteData>;
  public readonly context$: Observable<ITeamsContext>;

  protected constructor(
    protected readonly store: Store,
    protected readonly routerService: RouterService,
    protected readonly teamsService: IMicrosoftTeamsContextAccessor,
    protected readonly modalService: ModalService,
    protected readonly translateObserverService: TranslationObserverService,
  ) {
    this.routeData$ = routerService.getData$<AppRouteData>().pipe(
      map((data) => ({
        loadContext: data.loadContext === undefined ? true : data.loadContext,
        loadRole: data.loadRole === undefined ? true : data.loadRole,
      })),
      publishReplay(1),
      refCount(),
    );

    this.context$ = this.store.select(TeamState.context).pipe(skipWhile((context) => !context));
  }

  public abstract initialize(): Promise<Subscription>;

  protected startTranslations(): Subscription {
    const subscription$ = new Subscription();
    subscription$.add(this.translateObserverService.startUpdateSequence().subscribe());
    subscription$.add(new Subscription(() => this.modalService.ngOnDestroy()));
    subscription$.add(new Subscription(() => this.translateObserverService.ngOnDestroy()));
    return subscription$;
  }

  protected startTrackThemeChanges(): Subscription {
    return this.teamsService
      .getClientInfo$()
      .pipe(
        take(1),
        switchMap((x) =>
          x !== ClientType.Browser
            ? this.teamsService.getThemeInformation$().pipe(
                map((theme) => {
                  switch (theme) {
                    case 'dark':
                      return ChartTheme.Dark;
                    case 'contrast':
                      return ChartTheme.HighContrast;
                    default:
                      return ChartTheme.Default;
                  }
                }),
              )
            : this.store.dispatch(new CommonThemeActions.Initialize()).pipe(switchMap(() => this.store.select(CommonThemeState.theme))),
        ),
        distinctUntilChanged(),
      )
      .subscribe((theme) => {
        this.store.dispatch(new CommonThemeActions.SetTheme(theme));
      });
  }
}
